import React, { useCallback, useRef } from 'react';
import { useWindowDimensions } from 'react-native';
import { Extrapolation, interpolate, useSharedValue } from 'react-native-reanimated';
import Carousel, { ICarouselInstance, Pagination } from 'react-native-reanimated-carousel';

import { EventHudScoreboard } from '@/components/event-hud/EventHudScoreboard';
import { Box } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { ScoreboardTeamOverride } from '@/components/scoreboard/types';
import { designSystem } from '@/styles/styles';
import { MAX_WEB_WIDTH } from '@/utils/constants-platform-specific';

import { EventHudDetails, HudEvent } from './EventHudDetails';

type CarouselEventHudProps = {
    event: HudEvent;
    pamEventId: string;
    teamOverride?: ScoreboardTeamOverride;
};

const paginationContainerStyle = {
    gap: 6,
};

const basePaginationDotStyle = {
    height: 6,
    borderRadius: 60,
};

const ACTIVE_DOT_WIDTH = 20;
const INACTIVE_DOT_WIDTH = 6;

export const CarouselEventHud = ({ event, pamEventId, teamOverride }: CarouselEventHudProps) => {
    const { width } = useWindowDimensions();
    const scrollProgress = useSharedValue<number>(0);
    const carouselRef = useRef<ICarouselInstance | null>(null);

    const onPressPagination = useCallback((index: number) => {
        if (carouselRef.current) {
            carouselRef.current.scrollTo({ index, animated: true });
        }
    }, []);

    if (!event) {
        return null;
    }

    const items = [
        <EventHudDetails key="details" event={event} pamEventId={pamEventId} />,
        <EventHudScoreboard key="scoreboard" pamEventId={pamEventId} teamOverride={teamOverride} />,
    ];

    const screenWidth = MAX_WEB_WIDTH > width ? width : MAX_WEB_WIDTH;

    return (
        <Box flex={1} alignItems={'center'} pb="s16">
            <Carousel
                ref={carouselRef}
                data={items}
                renderItem={({ item }) => item}
                width={screenWidth}
                loop={false}
                autoPlay={false}
                onProgressChange={scrollProgress}
            />
            {/* Page control sits flush against the 118px carousel slot; py='s4' gives the 14px row height per design. */}
            <Box py="s4">
                <Pagination.Custom<{ index: number }>
                    progress={scrollProgress}
                    data={items.map((_, index) => ({ index }))}
                    dotStyle={{
                        ...basePaginationDotStyle,
                        width: INACTIVE_DOT_WIDTH,
                        backgroundColor: designSystem.colors.neutral300,
                    }}
                    activeDotStyle={{
                        ...basePaginationDotStyle,
                        width: ACTIVE_DOT_WIDTH,
                        backgroundColor: ds.colorValue.background.neutral.inverse,
                    }}
                    containerStyle={paginationContainerStyle}
                    customReanimatedStyle={(progress, index) => {
                        const val = Math.abs(progress - index);
                        return {
                            width: interpolate(
                                val,
                                [0, 1],
                                [ACTIVE_DOT_WIDTH, INACTIVE_DOT_WIDTH],
                                Extrapolation.CLAMP
                            ),
                        };
                    }}
                    onPress={onPressPagination}
                />
            </Box>
        </Box>
    );
};
